// @flow

import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { CSSTransition } from 'react-css-transition';
import tracker from 'utils/tracker';
import classNames from 'classnames';

import { List as ImmutableList, Map as ImmutableMap, OrderedMap } from 'immutable';
import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import { withStyles } from '@material-ui/core';
import Fade from '@material-ui/core/Fade';
import CircularProgress from '@material-ui/core/CircularProgress';
import Skeleton from '@material-ui/lab/Skeleton';

import zeroStateIcon from 'assets/zero-state-images/bank.svg';
import navIconInvestments from 'assets/nav-menu/investments.svg';

import { getAccounts, getInvestmentAccounts } from 'data/accounts/actions';
import { getAccountNodesTree } from 'data/accountNodes/selectors';
import * as accountSelectors from 'data/accounts/selectors';

import { isOldEdge } from 'utils/utils';
import { isQuicken } from 'isAcme';

import AccountRow from './AccountRow';
import { makeDisplayOptions } from './types';
import type { DisplayOptions } from './types';


const styles = (theme) => ({
  root: {
    width: '100%',
    background: theme.components.accountDrawer.background,
    padding: 0,
    borderBottom: 'none',
  },
  rootWithBorderBottom: {
    width: '100%',
    background: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.components.accountDrawer.divider}`,
    padding: 0,
    '&.dim': {
      opacity: 0.3,
    },
  },
  dividerRoot: {
    backgroundColor: theme.components.accountDrawer.divider,
  },
  loadingStateContainer: {
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 20,
  },
  zeroStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 271,
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 18,
    color: theme.palette.greyScale[2],
  },
  zeroStateTextHeadline: {
    marginBottom: 10,
    fontWeight: 500,
    color: theme.palette.greyScale[7],
  },
  zeroStateTextBody: {
    maxWidth: '80%',
    padding: 16,
    textAlign: 'center',
    color: theme.palette.greyScale[7],
  },
  skeletonWrapper: {
    height: '100%',
    width: '100%',
  },
  skeletonBlock: {
    backgroundColor: theme.palette.greyScale[5],
    borderRadius: 8,
    marginBottom: 10,
    marginLeft: '24px',
    width: '75%',
    height: 30,
    '&.large': {
      marginLeft: '24px',
      width: '85%',
      height: 40,
    },
    '&.small': {
      marginLeft: '24px',
      width: '70%',
      height: 20,
    },
    '&.netWorth': {
      marginLeft: '24px',
      width: '85%',
      height: 60,
    },
  },
  skeletonGroupWrapper: {
    marginBottom: '24px',
  },
})

const isIe = require('is-iexplorer') || isOldEdge();  // eslint-disable-line

type Props = {
  dimAccountList: bool,
  accountNode: Object,
  accountNodeName: string,
  accountNodesTree: any,
  borderBottom: any,
  classes: any,
  theme: any,
  balanceType: string,
  isLoading: bool,
  onSelect: Function,
  selectedNodeId: string,
  showColors: bool,
  omitCents: bool,
};

type State = {
  displayOptionsMap: ImmutableMap<string, DisplayOptions>,
};

class AccountList extends React.PureComponent<Props, State> {

  componentWillMount() {
    this.setState({
      displayOptionsMap: this.mkDisplayOptionsMap(this.props),
    });
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.accountNodesTree !== this.props.accountNodesTree) {
      const displayOptionsMap = this.mkDisplayOptionsMap(nextProps);
      this.setState({ displayOptionsMap });
    }
  }

  mkDisplayOptionsMapEntry = (path, node, props) => {
    let children = null;
    let expandParent = true;

    if (node.children) {
      children = OrderedMap(node.children.map((child) => {
        const pathToChild = path.concat(['children', child.id]);
        const displayNodeEntry = this.mkDisplayOptionsMapEntry(pathToChild, child, props);
        if (isQuicken && (path.includes('SEPARATE') || path.includes('HIDDEN'))) { // start separate and hidden sections as collapsed
          expandParent = false;
        }
        if (displayNodeEntry[1].selected) {
          expandParent = true;
        }
        return displayNodeEntry;
      }));
    }

    const displayOptions = makeDisplayOptions({
      selected: false,
      expand: expandParent,

      path: ImmutableList(path),
      children,
    });

    return [node.id, displayOptions];
  };

  mkDisplayOptionsMap = (props): ImmutableMap<string, DisplayOptions> => (
    ImmutableMap(props.accountNodesTree.map((node) =>
      this.mkDisplayOptionsMapEntry([node.id], node, props)))
  );

  handleExpandCollapse = (displayOptions) => {
    if (!displayOptions.children) {
      // cannot expand/collapse non-parent nodes
      return;
    }

    this.setState({
      displayOptionsMap: this.state.displayOptionsMap.setIn(
        displayOptions.path,
        displayOptions.set('expand', !displayOptions.expand)
      ),
    });

    const trackObj = {
      state: displayOptions.expand ? 'collapsed' : 'expanded',
      level: displayOptions.path.size === 3 ? 2 : 1,
    };
    tracker.track(tracker.events.accountListArrow, trackObj);
  };

  renderDrawerSkeleton = () => {
    const { classes } = this.props;
    const skeletonDrawer = [];
    for(let i = 0; i < 5; i+=1){
      skeletonDrawer.push(
        <div key={`skeleton-${i}`} className={classes.skeletonGroupWrapper}>
          <Skeleton variant='rect' className={classNames(classes.skeletonBlock, 'large')}/>
          <Skeleton variant='rect' className={classes.skeletonBlock}/>
          <Skeleton variant='rect' className={classNames(classes.skeletonBlock, 'small')}/>
        </div>
      );
    }

    return(
      <>
        <div className={classes.skeletonWrapper}>
          <Skeleton variant='rect' className={classNames(classes.skeletonBlock, 'netWorth')}/>
          {skeletonDrawer}
        </div>
      </>
    );
  };

  // ==============================================================================
  // RENDER
  // ==============================================================================

  render() {
    const {
      dimAccountList,
      accountNode,
      accountNodeName,
      accountNodesTree,
      borderBottom,
      classes,
      isLoading,
      onSelect,
      selectedNodeId,
      showColors,
      balanceType,
      theme,
      omitCents,
    } = this.props;

    const numAccounts = isLoading ? 0 : accountNodesTree.size;

    return (
      <div id="qCard-account-list">
        {(isLoading /* && !isAcme */ ) &&
        <div className={classes.loadingStateContainer}>
          <Fade
            in={isLoading}
            style={{
              transitionDelay: isLoading ? '800ms' : '0ms',
            }}
            unmountOnExit
          >
            <CircularProgress style={{ color: theme.palette.primaryBlue }} />
          </Fade>
        </div>
        }
        {/* This skeleton for the accountDrawer on the Transactions page for acme is currently not in use. It may be added in the future */}
        {(isLoading && false/* && isAcme */ ) &&
          this.renderDrawerSkeleton()
        }
        {!isLoading && numAccounts > 0 &&
        <List
          dense
          classes={{
            root: borderBottom ? classes.rootWithBorderBottom : classes.root,
          }}
          className={ dimAccountList ? 'dim' : ''}
        >
          {accountNodesTree.map((node, i) => {
            // log.debug('NODE: ', node.toJS());
            if (node.hide) {
              return (<div key={node.id} />);
            }
            return (
              <CSSTransition
                key={node.id}
                transitionAppear={!isIe}
                enterInitStyle={{ opacity: 0, transform: 'translateY(30px)' }}
                enterStyle={{
                  opacity: 1,
                  transform: 'translateY(0)',
                  transition: 'all 500ms ease',
                  transitionDelay: `${i * 0.05}s`,
                }}
                active
              >
                <AccountRow
                  id={`${node.displayLabel}-${i}`}
                  node={node}
                  dense
                  displayOptions={this.state.displayOptionsMap.get(node.id)}
                  onClick={onSelect}
                  onExpandCollapse={this.handleExpandCollapse}
                  selectedNodeId={selectedNodeId}
                  style={{ transitionDelay: `${i * 0.05}s` }}
                  showColors={showColors}
                  balanceType={balanceType}
                  omitCents={omitCents}
                />
              </CSSTransition>
            );
          })}
          {borderBottom &&
          <Divider
            classes={{
              root: classes.dividerRoot,
            }}
          />
          }
        </List>
        }
        {!isLoading && numAccounts === 0 && !accountNodeName &&
        <div className={classes.zeroStateContainer}>
          <img alt="no transactions" src={zeroStateIcon} />
          <br />
          <div className={classes.zeroStateTextHeadline}>No synced financial accounts</div>
          <div className={classes.zeroStateTextBody}>All your connected financial accounts will be displayed here.</div>
        </div>
        }
        {accountNodeName === 'INVESTMENT' && !isLoading && !accountNode &&
        <div className={classes.zeroStateContainer}>
          <img alt="no investments" src={navIconInvestments} />
          <br />
          <div className={classes.zeroStateTextHeadline}>No synced investment accounts</div>
          <div className={classes.zeroStateTextBody}>All your connected investment accounts will be displayed here.</div>
        </div>
        }
      </div>
    );
  }
}

function mapStateToProps(state, props) {
  return {
    isLoading: accountSelectors.getLoadPending(state),
    accountNodesTree: getAccountNodesTree(state, props),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    getAccounts: () => dispatch(getAccounts()),
    getInvestmentAccounts: () => dispatch(getInvestmentAccounts()),
  };
}

export default compose(
  withStyles(styles, {withTheme: true}),
  connect(mapStateToProps, mapDispatchToProps),
)(AccountList);
